import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DocumentTextIcon, ChevronDownIcon, PencilSquareIcon, TrashIcon, ArrowUpIcon, PlusIcon, PaperAirplaneIcon,
  ChatBubbleLeftRightIcon, CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import billingService from '../../services/billingService';
import { errText } from '../../services/scheduleService';
import TariffLines, { Issue, rands } from './TariffLines';
import TariffPicker from './TariffPicker';
import ItemLines from './ItemLines';
import Icd10Picker from './Icd10Picker';
import ClaimTracker, { statusOf } from './ClaimTracker';
import SurgeonPicker from '../surgeons/SurgeonPicker';

const CONFIDENCE = {
  high: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-red-100 text-red-700',
};
const PROCEDURE_KINDS = ['surgical', 'anaesthetic_procedure', 'consultation'];
const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
const sexWord = { M: 'Male', F: 'Female' };

/**
 * InvoicePreview — an invoice from the coding workflow: ICD-10 diagnoses and the
 * priced tariff lines. What the viewer may do comes from the server
 * (draft.permissions): the doctor corrects and sends it to billing; the billing
 * team reviews, corrects (the doctor is notified), asks the doctor a question,
 * then approves and submits (simulated for now). A final invoice is locked and
 * shows its claim status and history.
 *   onChange(draft): told whenever the invoice changes (lists refresh from it)
 *   trackLink: show a link to Billing & Claims once final (not on that page itself)
 */
const InvoicePreview = ({ draft: initial, onChange, trackLink = true }) => {
  const [draft, setDraftState] = useState(initial);
  useEffect(() => { setDraftState(initial); }, [initial]);
  const setDraft = (d) => { setDraftState(d); onChange?.(d); };

  const t = draft.tariff;
  const allIssues = [
    ...draft.issues, ...draft.lines.flatMap((l) => l.issues),
    ...(t ? [...t.issues, ...t.lines.flatMap((l) => l.issues)] : []),
    ...(draft.items || []).flatMap((i) => i.issues || []),
  ];
  const errors = allIssues.filter((i) => i.level === 'error').length;
  const s = statusOf(draft);
  const final = draft.status === 'final';

  return (
    <div className={`overflow-hidden rounded-xl border-2 bg-white ${final ? 'border-blue-200' : draft.status === 'discarded' ? 'border-slate-200 opacity-70' : draft.status === 'query' ? 'border-rose-200' : 'border-brand-teal/60'}`}>
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
        <DocumentTextIcon className="h-5 w-5 text-brand-teal" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-brand-deep">{final ? `Invoice ${draft.invoice_no}` : `Invoice #${draft.draft_id}`}</div>
          <div className="text-[11px] text-slate-500">{draft.doctor} · {fmtDate(draft.service_date)}</div>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${s.tone}`}>{s.label}</span>
      </div>

      {draft.status === 'query' && draft.query_note && (
        <div className="flex gap-2 border-b border-rose-100 bg-rose-50 px-4 py-2.5 text-sm text-rose-900">
          <ChatBubbleLeftRightIcon className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
          <div>
            <div><b>Question for {draft.doctor}:</b> {draft.query_note}</div>
            <div className="text-xs text-rose-700">Asked by {draft.query_by_name || 'the billing team'}. The doctor answers on their phone.</div>
          </div>
        </div>
      )}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 px-4 py-3 text-xs sm:grid-cols-4">
        <div>
          <dt className="text-slate-400">Patient</dt>
          <dd className="font-semibold text-slate-700">
            {draft.patient && <span className="block">{[draft.patient.title, draft.patient.first_names, draft.patient.surname].filter(Boolean).join(' ')}</span>}
            {[draft.patient_age_years != null && `${draft.patient_age_years} y`, sexWord[draft.patient_sex]].filter(Boolean).join(' · ') || 'Not stated'}
            {draft.patient && (
              <span className="block text-[11px] font-normal text-slate-500">
                {draft.patient.payer === 'private'
                  ? 'Private — patient pays'
                  : draft.patient.membership_number
                    ? `${draft.patient.scheme_name} · ${draft.patient.membership_number}/${draft.patient.dependant_code}`
                    : 'Medical aid — details missing'}
              </span>
            )}
          </dd>
        </div>
        <div className="col-span-1 sm:col-span-2"><dt className="text-slate-400">Procedure</dt><dd className="font-semibold text-slate-700">{draft.procedure_text || 'Not stated'}</dd></div>
        <div>
          <dt className="text-slate-400">Anaesthetic time</dt>
          <dd className="font-semibold text-slate-700">
            {draft.duration_minutes != null ? `${draft.duration_minutes} min${draft.case_start ? ` (${draft.case_start}–${draft.case_end})` : ''}` : '—'}
          </dd>
        </div>
        {draft.hospital_name && (
          <div className="col-span-2">
            <dt className="text-slate-400">Where</dt>
            <dd className="font-semibold text-slate-700">{[draft.hospital_name, draft.theatre].filter(Boolean).join(' · ')}</dd>
          </div>
        )}
        <ReferringSurgeon draft={draft} onDraft={setDraft} />
        <Authorisation draft={draft} onDraft={setDraft} />
        {draft.quote && <QuoteSummary quote={draft.quote} />}
      </dl>

      <DiagnosisTable draft={draft} onDraft={setDraft} />

      {draft.issues.length > 0 && (
        <div className="space-y-1 border-t border-slate-100 px-4 py-2.5">{draft.issues.map((i) => <Issue key={i.message} issue={i} />)}</div>
      )}

      <TariffSection draft={draft} onDraft={setDraft} />
      <ItemLines draft={draft} onDraft={setDraft} />

      {final ? (
        <>
          <ClaimTracker draft={draft} onDraft={setDraft} />
          {trackLink && (
            <div className="border-t border-slate-100 px-4 py-2 text-right text-xs">
              <Link to="/billing" className="font-semibold text-brand-teal hover:underline">Track it in Billing &amp; Claims →</Link>
            </div>
          )}
        </>
      ) : (
        <Footer draft={draft} onDraft={setDraft} errors={errors} />
      )}
    </div>
  );
};

// The estimate the patient was given before surgery (informed financial consent).
const QUOTE_WORD = { sent: 'sent, not answered yet', expired: 'expired without an answer', accepted: 'accepted', declined: 'declined' };
const QuoteSummary = ({ quote }) => (
  <div className={`col-span-2 rounded-lg px-2.5 py-1.5 sm:col-span-4 ${quote.status === 'accepted' ? 'bg-green-50 text-green-900' : quote.status === 'declined' ? 'bg-red-50 text-red-900' : 'bg-slate-50 text-slate-700'}`}>
    <dt className="text-[11px] opacity-70">Quote {quote.quote_no}</dt>
    <dd className="font-semibold">
      Estimate {rands(quote.practice_total)}{quote.shortfall != null ? `, patient pays about ${rands(quote.shortfall)}` : ''} — {QUOTE_WORD[quote.status] || quote.status}
      {quote.response_name && quote.status === 'accepted' ? ` by ${quote.response_name}` : ''}
      {quote.responded_at ? ` on ${new Date(quote.responded_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}` : ''}
      {quote.response_note && <span className="block text-xs font-normal">“{quote.response_note}”</span>}
    </dd>
  </div>
);

// The referring surgeon — their practice number goes on the claim.
const ReferringSurgeon = ({ draft, onDraft }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const editable = !!draft.permissions?.edit;
  const change = async (s) => {
    setBusy(true);
    setErr('');
    try {
      onDraft(await billingService.setSurgeon(draft.draft_id, s?.surgeon_id ?? null));
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="col-span-2">
      <dt className="text-slate-400">Referring surgeon</dt>
      <dd className={busy ? 'pointer-events-none opacity-60' : ''}>
        {editable ? (
          <SurgeonPicker
            compact
            value={draft.surgeon_id ? { surgeon_id: draft.surgeon_id, display_name: draft.surgeon, practice_number: draft.referring_practice_number } : null}
            fallbackName={draft.surgeon}
            onChange={change}
          />
        ) : (
          <span className="font-semibold text-slate-700">
            {draft.surgeon || 'Not recorded'}
            {draft.referring_practice_number
              ? <span className="ml-1 font-mono text-[11px] font-normal text-slate-500">· {draft.referring_practice_number}</span>
              : <span className="ml-1 text-[11px] font-semibold text-amber-700">· no practice number</span>}
          </span>
        )}
        {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
      </dd>
    </div>
  );
};

// The scheme's authorisation number for the admission — on the claim, and kept on the booking too.
const Authorisation = ({ draft, onDraft }) => {
  const editable = !!draft.permissions?.edit;
  const [value, setValue] = useState(draft.auth_number || '');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setValue(draft.auth_number || ''); }, [draft.auth_number]);
  const medicalAid = draft.patient?.payer === 'medical_aid';

  const save = async () => {
    setBusy(true);
    setErr('');
    try {
      onDraft(await billingService.setAuthorisation(draft.draft_id, value.trim() || null));
      setEditing(false);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="col-span-2">
      <dt className="text-slate-400">Authorisation number</dt>
      <dd>
        {editing ? (
          <form onSubmit={(e) => { e.preventDefault(); save(); }} className="flex items-center gap-1.5">
            <input
              autoFocus value={value} onChange={(e) => setValue(e.target.value.toUpperCase())} maxLength={30} placeholder="e.g. 12345678"
              className="w-40 rounded-md border border-slate-300 px-2 py-1 font-mono text-xs focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
            />
            <button type="submit" disabled={busy} className="rounded-md bg-brand-navy px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
            <button type="button" onClick={() => { setEditing(false); setValue(draft.auth_number || ''); setErr(''); }} className="text-[11px] font-semibold text-slate-500">Cancel</button>
          </form>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            {draft.auth_number
              ? <span className="font-mono font-semibold text-slate-700">{draft.auth_number}</span>
              : <span className={`font-semibold ${medicalAid ? 'text-amber-700' : 'text-slate-400'}`}>{medicalAid ? 'None yet' : draft.patient ? 'Not needed (private)' : '—'}</span>}
            {editable && <button onClick={() => setEditing(true)} className="text-[11px] font-semibold text-brand-teal hover:underline">{draft.auth_number ? 'Change' : 'Add'}</button>}
          </span>
        )}
        {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
      </dd>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Diagnoses — editable while the viewer may change the invoice.
// ---------------------------------------------------------------------------
const DiagnosisTable = ({ draft, onDraft }) => {
  const editable = !!draft.permissions?.edit;
  const [open, setOpen] = useState(null);
  const [changing, setChanging] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const current = draft.lines.map((l) => ({ code: l.code, said: l.said }));
  const inUse = current.map((l) => l.code).filter(Boolean);

  const save = async (lines) => {
    setBusy(true);
    setErr('');
    try {
      onDraft(await billingService.setDiagnoses(draft.draft_id, lines));
      setChanging(null);
      setAdding(false);
      setOpen(null);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  const replace = (lineNo, code) => save(current.map((l, i) => (i + 1 === lineNo ? { code, said: l.said } : l)));
  const remove = (lineNo) => save(current.filter((_, i) => i + 1 !== lineNo));
  const makePrimary = (lineNo) => save([current[lineNo - 1], ...current.filter((_, i) => i + 1 !== lineNo)]);
  const add = (code) => save([...current, { code, said: null }]);

  const iconBtn = 'rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-brand-navy disabled:opacity-40';

  return (
    <div className={`overflow-x-auto ${busy ? 'pointer-events-none opacity-60' : ''}`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-y border-slate-100 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <th className="w-8 px-4 py-1.5 font-semibold">#</th>
            <th className="w-20 px-2 py-1.5 font-semibold">ICD-10</th>
            <th className="px-2 py-1.5 font-semibold">Diagnosis</th>
            <th className="w-24 px-4 py-1.5 text-right font-semibold">{editable ? '' : 'Match'}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {draft.lines.map((l) => (
            <tr key={l.line_no} className="align-top">
              <td className="px-4 py-2 text-xs text-slate-400">{l.line_no}</td>
              <td className="px-2 py-2">
                <div className={`font-mono font-bold ${l.code ? 'text-brand-navy' : 'text-red-600'}`}>{l.code || '—'}</div>
                <div className="text-[10px] font-semibold uppercase text-slate-400">{l.is_primary ? 'Primary' : l.kind === 'external_cause' ? 'Ext. cause' : 'Secondary'}</div>
              </td>
              <td className="px-2 py-2">
                {changing === l.line_no ? (
                  <Icd10Picker autoFocus exclude={inUse} onPick={(row) => replace(l.line_no, row.code)} onCancel={() => setChanging(null)} placeholder={`Replace ${l.code || 'the code'} — search a code or words…`} />
                ) : (
                  <div className="text-slate-800">{l.description || <span className="text-red-600">No matching code</span>}</div>
                )}
                <div className="text-xs text-slate-400">You said: “{l.said}”</div>
                {l.reason && <div className="text-xs text-slate-500">{l.reason}</div>}
                <div className="mt-1 space-y-0.5">{l.issues.map((i) => <Issue key={i.message} issue={i} />)}</div>
                {l.alternatives.length > 0 && (
                  <button onClick={() => setOpen(open === l.line_no ? null : l.line_no)} className="mt-1 inline-flex items-center gap-0.5 text-[11px] font-semibold text-brand-teal hover:underline">
                    {l.alternatives.length} alternative{l.alternatives.length === 1 ? '' : 's'}
                    <ChevronDownIcon className={`h-3 w-3 transition ${open === l.line_no ? 'rotate-180' : ''}`} />
                  </button>
                )}
                {open === l.line_no && (
                  <ul className="mt-1 space-y-0.5 rounded-md bg-slate-50 px-2 py-1.5">
                    {l.alternatives.map((a) => (
                      <li key={a.code} className="flex items-center gap-2 text-xs text-slate-600">
                        <span className="font-mono font-semibold text-brand-navy">{a.code}</span>
                        <span className="flex-1">{a.description}</span>
                        {editable && !inUse.includes(a.code) && (
                          <button onClick={() => replace(l.line_no, a.code)} className="rounded px-1.5 py-0.5 font-semibold text-brand-teal hover:bg-teal-50">Use</button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </td>
              <td className="px-4 py-2 text-right">
                {l.confidence && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${CONFIDENCE[l.confidence]}`}>{l.confidence}</span>}
                {editable && (
                  <div className="mt-1 flex justify-end gap-0.5">
                    <button onClick={() => setChanging(changing === l.line_no ? null : l.line_no)} title="Choose the code yourself" className={iconBtn}><PencilSquareIcon className="h-4 w-4" /></button>
                    {l.line_no > 1 && <button onClick={() => makePrimary(l.line_no)} title="Make this the primary diagnosis" className={iconBtn}><ArrowUpIcon className="h-4 w-4" /></button>}
                    {draft.lines.length > 1 && <button onClick={() => remove(l.line_no)} title="Remove this diagnosis" className={`${iconBtn} hover:text-red-600`}><TrashIcon className="h-4 w-4" /></button>}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editable && (
        <div className="px-4 py-2">
          {adding ? (
            <Icd10Picker autoFocus exclude={inUse} onPick={(row) => add(row.code)} onCancel={() => setAdding(false)} placeholder="Add a diagnosis — search a code or words…" />
          ) : (
            <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-teal hover:underline">
              <PlusIcon className="h-3.5 w-3.5" /> Add an ICD-10 code
            </button>
          )}
        </div>
      )}
      {err && <p className="px-4 pb-2 text-sm text-red-600">{err}</p>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Actions — whatever the viewer may do next: send to billing, ask the doctor,
// approve and submit (simulated), or discard.
// ---------------------------------------------------------------------------
const Footer = ({ draft, onDraft, errors }) => {
  const perm = draft.permissions || {};
  const [mode, setMode] = useState(null); // 'submit' | 'query' | 'send'
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  if (draft.status === 'discarded') {
    return <div className="border-t border-slate-100 px-4 py-2.5 text-right text-xs font-semibold text-slate-500">Discarded</div>;
  }

  const run = async (what, fn) => {
    setBusy(what);
    setErr('');
    try {
      onDraft(await fn());
      setMode(null);
      setNote('');
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy('');
    }
  };

  const p = draft.patient;
  const destination = !p
    ? 'submit it'
    : p.payer === 'private'
      ? `mark it as sent to ${[p.first_names, p.surname].join(' ')}`
      : `submit it to ${p.scheme_name || 'the medical aid'}${p.membership_number ? ` (${p.membership_number}/${p.dependant_code})` : ''}`;
  const submitLabel = draft.status === 'awaiting_review' ? 'Approve & submit' : 'Finalise & submit';
  const hint = errors
    ? `${errors} problem${errors === 1 ? '' : 's'} to fix before billing`
    : draft.status === 'awaiting_review'
      ? (perm.finalise ? 'Sent by the doctor — check the codes and the fee, then approve.' : 'With the billing team for review.')
      : draft.status === 'query'
        ? 'Waiting for the doctor to answer.'
        : perm.send_for_review ? 'Check the codes and the fee, then send it to billing.' : 'The doctor hasn’t sent this to billing yet.';
  const input = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30';

  return (
    <div className="border-t border-slate-100 px-4 py-2.5">
      {mode === 'submit' && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
          <p className="text-sm font-semibold text-blue-900">Save the final invoice and {destination}?</p>
          <p className="mt-0.5 text-xs text-blue-800">
            It gets an invoice number and can’t be edited afterwards{draft.uid !== Number(localStorage.getItem('uid')) ? ', and the doctor is told' : ''}. Submission is simulated — no claims switch is connected yet.
          </p>
          <div className="mt-2 flex justify-end gap-2">
            <button onClick={() => setMode(null)} disabled={!!busy} className="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-white">Cancel</button>
            <button onClick={() => run('final', () => billingService.finalise(draft.draft_id))} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60">
              <PaperAirplaneIcon className="h-4 w-4" /> {busy === 'final' ? 'Saving…' : submitLabel}
            </button>
          </div>
        </div>
      )}

      {mode === 'query' && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5">
          <p className="mb-1.5 text-sm font-semibold text-rose-900">Ask {draft.doctor} about this invoice</p>
          <textarea rows={2} autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Was this an emergency? The theatre times look long for a hernia repair." className={input} />
          <p className="mt-1 text-xs text-rose-700">The invoice goes back to the doctor, who is notified on their phone.</p>
          <div className="mt-2 flex justify-end gap-2">
            <button onClick={() => { setMode(null); setNote(''); }} disabled={!!busy} className="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-white">Cancel</button>
            <button onClick={() => run('query', () => billingService.query(draft.draft_id, note))} disabled={!!busy || !note.trim()} className="rounded-lg bg-rose-600 px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
              {busy === 'query' ? 'Sending…' : 'Send question'}
            </button>
          </div>
        </div>
      )}

      {mode === 'send' && (
        <div className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2.5">
          <p className="mb-1.5 text-sm font-semibold text-brand-deep">{draft.status === 'query' ? 'Answer and send it back to billing' : 'Send to the billing team'}</p>
          <textarea rows={2} autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder={draft.status === 'query' ? 'Your answer' : 'Anything the billing team should know (optional)'} className={input} />
          <div className="mt-2 flex justify-end gap-2">
            <button onClick={() => { setMode(null); setNote(''); }} disabled={!!busy} className="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-white">Cancel</button>
            <button onClick={() => run('send', () => billingService.send(draft.draft_id, note))} disabled={!!busy} className="rounded-lg bg-brand-navy px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
              {busy === 'send' ? 'Sending…' : 'Send to billing'}
            </button>
          </div>
        </div>
      )}

      {!mode && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={`text-xs ${errors ? 'font-semibold text-red-700' : 'text-slate-500'}`}>{hint}</span>
          <div className="flex flex-wrap gap-2">
            {perm.discard && (
              <button onClick={() => run('discard', () => billingService.review(draft.draft_id, 'discarded'))} disabled={!!busy} className="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50">
                {busy === 'discard' ? 'Discarding…' : 'Discard'}
              </button>
            )}
            {perm.query_doctor && (
              <button onClick={() => setMode('query')} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-1.5 text-sm font-semibold text-rose-700 hover:bg-rose-50">
                <ChatBubbleLeftRightIcon className="h-4 w-4" /> Ask the doctor…
              </button>
            )}
            {perm.send_for_review && (
              <button onClick={() => setMode('send')} disabled={!!busy} className="rounded-lg border border-brand-navy px-3 py-1.5 text-sm font-semibold text-brand-navy hover:bg-slate-50">
                Send to billing…
              </button>
            )}
            {perm.finalise && (
              <button onClick={() => setMode('submit')} disabled={!!busy || errors > 0} title={errors ? 'Fix the flagged problems first' : undefined} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-gradient px-4 py-1.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50">
                <CheckBadgeIcon className="h-4 w-4" /> {submitLabel}
              </button>
            )}
          </div>
        </div>
      )}
      {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tariff lines — fee inputs and procedure codes, editable while permitted.
// ---------------------------------------------------------------------------
const factsFrom = (draft) => ({
  duration_minutes: draft.duration_minutes == null ? '' : String(draft.duration_minutes),
  bmi: draft.tariff?.facts?.bmi == null ? '' : String(draft.tariff.facts.bmi),
  emergency: draft.tariff?.facts?.emergency === true,
  prone: draft.tariff?.facts?.prone === true,
});

const TariffSection = ({ draft, onDraft }) => {
  const t = draft.tariff;
  const editable = !!draft.permissions?.edit;
  const [facts, setFacts] = useState(() => factsFrom(draft));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setFacts(factsFrom(draft)); }, [draft]);

  if (!t) {
    return <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-400">No tariff lines — this invoice was made before fees were worked out.</div>;
  }

  const dirty = JSON.stringify(factsFrom(draft)) !== JSON.stringify(facts);
  const set = (k) => (e) => setFacts((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const procedureCodes = t.lines.filter((l) => PROCEDURE_KINDS.includes(l.kind) && l.code).map((l) => l.code);

  const setLineDx = async (lineNo, codes) => {
    setBusy(true);
    setErr('');
    try {
      onDraft(await billingService.setLineDiagnoses(draft.draft_id, lineNo, codes));
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const reprice = async (changes) => {
    setBusy(true);
    setErr('');
    try {
      onDraft(await billingService.reprice(draft.draft_id, changes));
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const applyFacts = () => reprice({
    duration_minutes: facts.duration_minutes === '' ? null : Number(facts.duration_minutes),
    // Unticked / blank means "not the case" — the modifier comes off.
    facts: { emergency: facts.emergency || null, prone: facts.prone || null, bmi: facts.bmi === '' ? null : Number(facts.bmi) },
  });

  const field = 'mt-0.5 block w-24 rounded-md border border-slate-300 px-2 py-1 text-sm font-normal text-slate-700 focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30';

  return (
    <div className="border-t border-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pb-2 pt-3">
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Tariff lines</h3>
        {t.fee_schedule_name && <span className="text-[11px] text-slate-400">Rates: {t.fee_schedule_name}</span>}
      </div>

      {editable && (
        <div className="mx-4 mb-3 space-y-2 rounded-lg bg-slate-50 px-3 py-2.5">
          <div className="flex flex-wrap items-end gap-3 text-[11px] font-semibold text-slate-500">
            <label>Anaesthetic time (min)<input type="number" min="0" value={facts.duration_minutes} onChange={set('duration_minutes')} className={field} /></label>
            <label>BMI<input type="number" min="10" step="0.1" value={facts.bmi} onChange={set('bmi')} className={field} /></label>
            <label className="flex items-center gap-1.5 pb-1.5 text-sm font-normal text-slate-600">
              <input type="checkbox" checked={facts.emergency} onChange={set('emergency')} className="rounded border-slate-300" /> Emergency
            </label>
            <label className="flex items-center gap-1.5 pb-1.5 text-sm font-normal text-slate-600">
              <input type="checkbox" checked={facts.prone} onChange={set('prone')} className="rounded border-slate-300" /> Prone
            </label>
            <button onClick={applyFacts} disabled={!dirty || busy} className="mb-0.5 rounded-lg bg-brand-navy px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">
              {busy ? 'Working out…' : 'Recalculate'}
            </button>
          </div>
          <TariffPicker onPick={(row) => reprice({ add: [row.code] })} disabled={busy} exclude={procedureCodes} placeholder="Add a procedure or anaesthetic item…" />
        </div>
      )}

      <TariffLines
        result={t}
        busy={busy}
        onSwap={editable ? (lineNo, code) => reprice({ codes: [{ line_no: lineNo, code }] }) : undefined}
        onRemove={editable ? (lineNo) => reprice({ remove: [lineNo] }) : undefined}
        diagnoses={draft.lines.filter((l) => l.code)}
        onSetDiagnoses={editable ? setLineDx : undefined}
      />
      {err && <p className="px-4 pb-2 text-sm text-red-600">{err}</p>}
    </div>
  );
};

export default InvoicePreview;
